import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = unauth, object = auth
  const [loading, setLoading] = useState(true);

  const checkSession = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    // Re-fetch /auth/me to get fully-normalized permission shape
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      return me.data;
    } catch {
      setUser(data);
      return data;
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(false);
  };

  const hasPerm = (module, action = "view") => {
    if (!user) return false;
    const p = user.permissions?.[module];
    if (p == null) return false;
    if (typeof p === "boolean") return p;
    if (typeof p === "object") return Boolean(p[action]);
    return false;
  };

  const can = hasPerm;

  const canManageHead = (head) => {
    if (!user) return false;
    const list = user.retention_heads || [];
    if (!list.length) return true;
    return list.includes(head);
  };

  const canAccessProject = (project) => {
    if (!user) return false;
    if (user.unrestricted_projects) return true;
    const list = user.allowed_projects || [];
    if (!list.length) return true;
    return list.includes(project);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPerm, can, canManageHead, canAccessProject, refresh: checkSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
